import type { ReactElement } from 'react';

import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

interface HeadingProps {
  /**
   * Level of header
   */
  level?: number;
  /**
   * Header get predefined paddings
   */
  hasPaddings?: boolean;
  /**
   * Header contents
   */
  children?: string | ReactElement;
  /**
   * Header anchor
   */
  anchor?: string;
  /**
   * Header alignment
   */
  alignment?: 'left' | 'center' | 'right';
  /**
   * Header color
   */
  color?: 'black' | 'blue' | 'darkBlue' | 'red' | 'white';
  /**
   * Additional classes for header (all levels)
   */
  additionalClasses?: string;
  /**
   * Heading Title on mouse-over used for long text
   */
  title?: string;
  /**
   * Component test id
   */
  testId?: string;
}

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const Heading = ({
  level = 1,
  children,
  anchor = '',
  alignment = 'left',
  color = 'darkBlue',
  hasPaddings = true,
  additionalClasses = '',
  title = '',
  testId = '',
}: HeadingProps) => {
  const safeLevel = Math.min(Math.max(level, 1), 6) as HeadingLevel;
  const HeaderToRender = `h${safeLevel}` as HeadingElement;
  const sizeClass = {
    1: ['text-5xl font-bold', hasPaddings && 'pt-6 pb-3'],
    2: ['text-4xl font-bold', hasPaddings && 'pt-5 pb-2.5'],
    3: ['text-3xl font-bold', hasPaddings && 'pt-4 pb-2'],
    4: ['text-2xl font-bold', hasPaddings && 'pt-3 pb-1.5'],
    5: ['text-xl font-bold', hasPaddings && 'pt-2 pb-1'],
    6: ['text-lg font-bold', hasPaddings && 'pt-1'],
  };

  const alignmentClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const colorClass = {
    black: 'text-black',
    blue: 'text-blue',
    darkBlue: 'text-slate-900',
    red: 'text-red',
    white: 'text-white',
  };

  return (
    <HeaderToRender
      className={twMerge(
        sizeClass[safeLevel],
        alignmentClass[alignment],
        colorClass[color],
        additionalClasses,
      )}
      id={anchor}
      title={title}
      {...getTestProps(testId)}
    >
      {children}
    </HeaderToRender>
  );
};

export default Heading;
